import {
  calculateUrunLaborTry,
  calculateUrunMaliyetHas,
  getUrunAdetIscilik,
  getUrunIscilik,
  usesAdetIscilikLabor,
  usesIscilikLabor
} from '../urunPricing';
import {
  ADDITIONAL_CALC,
  COST_METHOD,
  COST_SCOPE,
  COST_SCOPE_PRIORITY,
  COST_STATUS_CODE,
  FINANCE_COST_SCHEMA_VERSION,
  GOLD_RATE_SOURCE,
  LABOR_MODEL,
  QUALITY_ISSUE,
  emptyCostBreakdown
} from './financeCostConstants';
import { resolveGoldRate, toFiniteNumber } from './financeGoldRate';
import { resolveMilyem } from './financeMilyem';
import {
  computeTradeEconomics,
  getHamCatalogSpec,
  normalizeProductName,
  resolveHamCatalogCost,
  TAKI_DEFAULT_MILYEM,
  tradeSideOf
} from './financeHamVeri';

export function round2(n) {
  return Math.round((Number(n) || 0) * 100) / 100;
}

export function computeSaleEconomics({ saleAmountTry, totalCostTry, costComplete } = {}) {
  const sale = saleAmountTry == null || saleAmountTry === ''
    ? null
    : round2(toFiniteNumber(saleAmountTry));
  if (costComplete !== true || totalCostTry == null || sale == null) {
    return {
      saleAmountTry: sale,
      grossProfitTry: null,
      grossMarginPct: null
    };
  }
  const cost = round2(totalCostTry);
  const profit = round2(sale - cost);
  const margin = sale > 0 ? round2((profit / sale) * 100) : null;
  return {
    saleAmountTry: sale,
    grossProfitTry: profit,
    grossMarginPct: margin
  };
}

function norm(value) {
  return String(value || '').trim();
}

function isActiveRule(rule) {
  return rule && rule.active !== false;
}

function rulePriority(rule) {
  const scoped = COST_SCOPE_PRIORITY[rule?.scopeType] || 0;
  const extra = Number(rule?.priority) || 0;
  return scoped * 1000 + extra;
}

export function matchRuleToProduct(rule, product) {
  if (!isActiveRule(rule) || !product) return false;
  const scope = rule.scopeType;
  const scopeId = norm(rule.scopeId);

  if (scope === COST_SCOPE.PRODUCT) {
    return scopeId !== '' && (
      scopeId === norm(product.barkod) ||
      scopeId === norm(product.id)
    );
  }
  if (scope === COST_SCOPE.PRODUCT_TYPE) {
    return scopeId !== '' && (
      scopeId === norm(product.urunTipi) ||
      scopeId === norm(product.isim)
    );
  }
  if (scope === COST_SCOPE.CATEGORY) {
    return scopeId !== '' && scopeId === norm(product.kategori);
  }
  if (scope === COST_SCOPE.DEFAULT) {
    const ruleCat = norm(rule.category);
    if (!ruleCat) return true;
    return ruleCat === norm(product.kategori);
  }
  return false;
}

/**
 * Katmanlı kural seçimi.
 * 1 ürün  2 ürün tipi  3 kategori  4 varsayılan  5 yoksa null
 */
export function resolveProductCostRule(product, context = {}) {
  const rules = Array.isArray(context.rules) ? context.rules.filter(isActiveRule) : [];
  const layers = [
    COST_SCOPE.PRODUCT,
    COST_SCOPE.PRODUCT_TYPE,
    COST_SCOPE.CATEGORY,
    COST_SCOPE.DEFAULT
  ];

  for (const scope of layers) {
    const matches = rules
      .filter((rule) => rule.scopeType === scope && matchRuleToProduct(rule, product))
      .sort((a, b) => rulePriority(b) - rulePriority(a));
    if (matches.length) {
      return {
        rule: matches[0],
        layer: scope,
        unknown: false
      };
    }
  }

  return { rule: null, layer: null, unknown: true };
}

function isStoneCost(item) {
  const key = String(item?.key || item?.name || '').toLocaleLowerCase('tr-TR');
  return key.includes('taş') || key.includes('tas') || key.includes('stone');
}

export function calculateAdditionalCosts(items, { gram = 0, adet = 1 } = {}) {
  const list = Array.isArray(items) ? items : [];
  let stoneTry = 0;
  let additionalTry = 0;
  const lines = [];

  list.forEach((item) => {
    const value = toFiniteNumber(item?.value);
    if (!(value > 0)) return;
    let amount = 0;
    const calc = item?.calculationType || ADDITIONAL_CALC.FIXED;
    if (calc === ADDITIONAL_CALC.PER_GRAM) amount = value * toFiniteNumber(gram);
    else if (calc === ADDITIONAL_CALC.PER_UNIT) amount = value * (toFiniteNumber(adet) || 1);
    else amount = value;
    amount = round2(amount);
    lines.push({
      name: item.name || item.key || 'Ek',
      calculationType: calc,
      value,
      amount
    });
    if (isStoneCost(item)) stoneTry += amount;
    else additionalTry += amount;
  });

  return {
    stoneTry: round2(stoneTry),
    additionalTry: round2(additionalTry),
    lines
  };
}

function gramOf(product, islem) {
  const fromIslem = toFiniteNumber(islem?.gram);
  if (fromIslem > 0) return fromIslem;
  return toFiniteNumber(product?.gram);
}

function adetOf(islem, product) {
  const fromIslem = toFiniteNumber(islem?.adet);
  if (fromIslem > 0) return fromIslem;
  return 1;
}

function laborFromProduct(product, kur) {
  const laborTry = round2(calculateUrunLaborTry(product, kur));
  let model = LABOR_MODEL.NONE;
  if (usesIscilikLabor(product)) model = LABOR_MODEL.MILYEM;
  else if (usesAdetIscilikLabor(product)) model = LABOR_MODEL.ADET_MILYEM;
  return { laborTry, model };
}

function laborFromRule(rule, product, { gram, adet, kur }) {
  const model = rule?.laborModel || LABOR_MODEL.INHERIT;
  if (model === LABOR_MODEL.NONE) return { laborTry: 0, model };
  if (model === LABOR_MODEL.PER_GRAM_TRY) {
    return { laborTry: round2(toFiniteNumber(rule.laborValueTry) * gram), model };
  }
  if (model === LABOR_MODEL.FIXED_TRY) {
    return { laborTry: round2(toFiniteNumber(rule.laborValueTry) * (adet || 1)), model };
  }
  if (model === LABOR_MODEL.MILYEM || model === LABOR_MODEL.ADET_MILYEM || model === LABOR_MODEL.INHERIT) {
    return laborFromProduct(product, kur);
  }
  return laborFromProduct(product, kur);
}

export function collectQualityIssues({ product, islem, rule, rate, method, settings, rateContext } = {}) {
  const issues = [];
  const kategori = norm(product?.kategori || islem?.kategori);
  const gram = gramOf(product, islem);
  const milyem = resolveMilyem({ product, rule, settings });

  if (!rule && method !== COST_METHOD.HAM_VERI_SELL) issues.push(QUALITY_ISSUE.MISSING_RULE);

  if (method === COST_METHOD.GOLD_FORMULA) {
    if (!(gram > 0)) issues.push(QUALITY_ISSUE.MISSING_GRAM);
    if (!milyem.ok) issues.push(QUALITY_ISSUE.MISSING_MILYEM);
    if (!rate?.ok) issues.push(QUALITY_ISSUE.MISSING_RATE);
    const iscilik = getUrunIscilik(product);
    const adetIscilik = getUrunAdetIscilik(product);
    if (iscilik < 0 || adetIscilik < 0) issues.push(QUALITY_ISSUE.BAD_LABOR);
  }

  if (method === COST_METHOD.MANUAL_TRY) {
    if (!(toFiniteNumber(rule?.manualCostTry) > 0)) issues.push(QUALITY_ISSUE.MANUAL_ZERO);
  }

  if (method === COST_METHOD.UNIT_COST) {
    if (!(toFiniteNumber(rule?.unitCostTry) > 0)) issues.push(QUALITY_ISSUE.MISSING_UNIT_COST);
  }

  if (method === COST_METHOD.FX_COST) {
    if (!(toFiniteNumber(rule?.costRate ?? rule?.manualCostRate) > 0)) {
      issues.push(QUALITY_ISSUE.MISSING_FX_RATE);
    }
  }

  if (method === COST_METHOD.HAM_VERI_SELL) {
    const ham = resolveHamCatalogCost({ product, islem, prices: rateContext?.prices, rule });
    if (!ham.ok) issues.push(QUALITY_ISSUE.MISSING_RATE);
  }

  if (kategori === 'Takı Ürün' && !norm(product?.barkod) && !product?.isCatalog) {
    issues.push(QUALITY_ISSUE.MISSING_BARCODE);
  }

  return issues;
}

function statusFrom({ method, issues, unknown }) {
  if (unknown || !method) return COST_STATUS_CODE.MISSING;
  if (issues.includes(QUALITY_ISSUE.MISSING_RULE)) return COST_STATUS_CODE.MISSING;
  const blocking = issues.filter((i) => i !== QUALITY_ISSUE.MISSING_BARCODE);
  if (blocking.length) return COST_STATUS_CODE.NEEDS_REVIEW;
  if (method === COST_METHOD.MANUAL_TRY) return COST_STATUS_CODE.MANUAL;
  return COST_STATUS_CODE.AUTOMATIC;
}

function inboundPurchaseEligible(islem) {
  const tur = norm(islem?.islemTuru);
  return (tur === 'Alış' || tur === 'İade') && toFiniteNumber(islem?.toplamTutar) > 0;
}

/**
 * Maliyeti hesaplar. Eksik veride sayı uydurmaz: costComplete=false, totalCostTry=null.
 */
export function calculateFinanceCost({ product, islem = {}, rule, rateContext } = {}) {
  const resolved = rule ? { rule, unknown: false, layer: rule.scopeType } : resolveProductCostRule(product, rateContext);
  const usedRule = resolved.rule;
  let method = usedRule?.costMethod || null;
  const explicitNonHam = Boolean(usedRule?.costMethod && usedRule.costMethod !== COST_METHOD.HAM_VERI_SELL);
  const hamQuote = resolveHamCatalogCost({
    product,
    islem,
    prices: rateContext?.prices,
    rule: explicitNonHam ? null : usedRule
  });

  if (!explicitNonHam && hamQuote.applicable) {
    method = COST_METHOD.HAM_VERI_SELL;
  }

  if (!method && inboundPurchaseEligible(islem) && product?.isCatalog) {
    method = COST_METHOD.PURCHASE_AMOUNT;
  }

  const rate = resolveGoldRate({
    source: usedRule?.goldRateSource || rateContext?.defaultGoldRateSource,
    rule: usedRule,
    product,
    rateContext
  });

  const gram = gramOf(product, islem);
  const adet = adetOf(islem, product);
  const extras = calculateAdditionalCosts(usedRule?.additionalCosts, { gram, adet });
  const settings = rateContext?.settings;
  const resolvedMilyem = resolveMilyem({ product, rule: usedRule, settings });
  const issues = collectQualityIssues({ product, islem, rule: usedRule, rate, method, settings, rateContext });
  const saleAmountTry = islem?.toplamTutar == null || islem.toplamTutar === ''
    ? null
    : round2(toFiniteNumber(islem.toplamTutar));
  const tradeSide = tradeSideOf(islem);

  const breakdown = emptyCostBreakdown();
  let currency = null;
  let costRate = null;
  let saleRate = toFiniteNumber(islem?.hasFiyati) || null;
  let quantity = adet || gram || null;

  const fail = (extraIssues = []) => {
    const allIssues = Array.from(new Set([...issues, ...extraIssues]));
    const status = statusFrom({ method, issues: allIssues, unknown: resolved.unknown && !method });
    return {
      ok: false,
      costComplete: false,
      status,
      issues: allIssues.length ? allIssues : [QUALITY_ISSUE.MISSING_RULE],
      method: method || null,
      rule: usedRule,
      layer: resolved.layer,
      goldRate: rate,
      gram,
      adet,
      milyem: resolvedMilyem.ok ? resolvedMilyem.milyem : 0,
      milyemSource: resolvedMilyem.source,
      breakdown,
      extras,
      totalCostTry: null,
      currency,
      costRate,
      saleRate,
      saleAmountTry,
      quantity,
      tradeSide
    };
  };

  if (!method) return fail();

  if (method === COST_METHOD.GOLD_FORMULA) {
    if (!(gram > 0) || !resolvedMilyem.ok || !rate.ok) {
      return fail();
    }
    const priced = {
      ...product,
      gram,
      urunMilyem: resolvedMilyem.milyem
    };
    const goldTry = round2(gram * toFiniteNumber(priced.urunMilyem) * rate.rate);
    const inheritLabor = !usedRule?.laborModel || usedRule.laborModel === LABOR_MODEL.INHERIT
      || usedRule.laborModel === LABOR_MODEL.MILYEM
      || usedRule.laborModel === LABOR_MODEL.ADET_MILYEM;
    const labor = inheritLabor
      ? laborFromProduct(priced, rate.rate)
      : laborFromRule(usedRule, priced, { gram, adet, kur: rate.rate });
    if (inheritLabor) {
      const formulaTotal = round2(calculateUrunMaliyetHas(priced) * rate.rate);
      breakdown.goldTry = round2(formulaTotal - labor.laborTry);
      breakdown.laborTry = labor.laborTry;
    } else {
      breakdown.goldTry = goldTry;
      breakdown.laborTry = labor.laborTry;
    }
    breakdown.stoneTry = extras.stoneTry;
    breakdown.additionalTry = extras.additionalTry;
    breakdown.totalTry = round2(
      breakdown.goldTry + breakdown.laborTry + extras.stoneTry + extras.additionalTry
    );
    const status = statusFrom({ method, issues, unknown: false });
    return {
      ok: status === COST_STATUS_CODE.AUTOMATIC || status === COST_STATUS_CODE.MANUAL,
      costComplete: issues.filter((i) => i !== QUALITY_ISSUE.MISSING_BARCODE).length === 0,
      status,
      issues,
      method,
      rule: usedRule,
      layer: resolved.layer,
      goldRate: rate,
      gram,
      adet,
      milyem: resolvedMilyem.milyem,
      milyemSource: resolvedMilyem.source,
      laborModel: labor.model,
      breakdown,
      extras,
      totalCostTry: breakdown.totalTry,
      currency: 'TRY',
      costRate: rate.rate,
      saleRate,
      saleAmountTry,
      quantity: gram
    };
  }

  if (method === COST_METHOD.HAM_VERI_SELL) {
    if (!hamQuote.ok) return fail([QUALITY_ISSUE.MISSING_RATE]);
    breakdown.goldTry = hamQuote.totalCostTry;
    breakdown.totalTry = hamQuote.totalCostTry;
    const hamIssues = issues.filter((i) => i !== QUALITY_ISSUE.MISSING_RULE);
    const status = statusFrom({ method, issues: hamIssues, unknown: false });
    const economics = computeTradeEconomics({
      amountTry: saleAmountTry,
      referenceCostTry: hamQuote.totalCostTry,
      costComplete: hamIssues.length === 0,
      side: tradeSide
    });
    return {
      ok: hamIssues.length === 0,
      costComplete: hamIssues.length === 0,
      status,
      issues: hamIssues,
      method,
      rule: usedRule,
      layer: resolved.layer || COST_SCOPE.PRODUCT_TYPE,
      goldRate: rate,
      gram: hamQuote.gram || gram,
      adet: hamQuote.adet,
      milyem: hamQuote.spec?.milyem || 0,
      milyemSource: hamQuote.spec?.kind === 'melt' ? 'catalog' : null,
      breakdown,
      extras,
      totalCostTry: hamQuote.totalCostTry,
      currency: 'TRY',
      costRate: hamQuote.unitCostTry,
      saleRate: hamQuote.spec?.quoteMode === 'buy' || (hamQuote.spec?.quoteMode === 'side' && tradeSide === 'buy')
        ? (hamQuote.quote?.buy || null)
        : (hamQuote.quote?.sell || null),
      saleAmountTry,
      quantity: hamQuote.adet,
      tradeSide,
      hamCode: hamQuote.spec?.hamCode || null,
      hamMultiplier: hamQuote.spec?.effectiveMultiplier || hamQuote.spec?.multiplier || null,
      // Kullanilan ham kotasyon: alis tarafinda ham alis, satista ham satis
      // (kullanici karari 2026-09-29: elimizdeki malin degeri, toptanciya
      // bozdurdugumuzda alacagimiz fiyattir).
      hamSellTry: hamQuote.spec?.quoteMode === 'buy' || (hamQuote.spec?.quoteMode === 'side' && tradeSide === 'buy')
        ? (hamQuote.quote?.buy || null)
        : (hamQuote.quote?.sell || null),
      grossProfitTry: economics.grossProfitTry,
      grossMarginPct: economics.grossMarginPct
    };
  }

  if (method === COST_METHOD.MANUAL_TRY) {
    const manual = toFiniteNumber(usedRule?.manualCostTry);
    if (!(manual > 0)) return fail([QUALITY_ISSUE.MANUAL_ZERO]);
    breakdown.additionalTry = extras.additionalTry;
    breakdown.stoneTry = extras.stoneTry;
    breakdown.totalTry = round2(manual + extras.stoneTry + extras.additionalTry);
    return {
      ok: true,
      costComplete: true,
      status: COST_STATUS_CODE.MANUAL,
      issues,
      method,
      rule: usedRule,
      layer: resolved.layer,
      goldRate: rate,
      gram,
      adet,
      milyem: resolvedMilyem.ok ? resolvedMilyem.milyem : 0,
      milyemSource: resolvedMilyem.source,
      breakdown,
      extras,
      totalCostTry: breakdown.totalTry,
      currency: 'TRY',
      costRate: null,
      saleRate,
      saleAmountTry,
      quantity: adet || gram
    };
  }

  if (method === COST_METHOD.UNIT_COST) {
    const unit = toFiniteNumber(usedRule?.unitCostTry);
    if (!(unit > 0)) return fail([QUALITY_ISSUE.MISSING_UNIT_COST]);
    const qty = kategoriUsesGram(product, islem) ? (gram > 0 ? gram : adet) : (adet > 0 ? adet : 1);
    if (!(qty > 0)) return fail([QUALITY_ISSUE.MISSING_GRAM]);
    const base = round2(unit * qty);
    breakdown.additionalTry = extras.additionalTry;
    breakdown.stoneTry = extras.stoneTry;
    breakdown.totalTry = round2(base + extras.stoneTry + extras.additionalTry);
    return {
      ok: true,
      costComplete: true,
      status: COST_STATUS_CODE.AUTOMATIC,
      issues,
      method,
      rule: usedRule,
      layer: resolved.layer,
      goldRate: rate,
      gram,
      adet,
      milyem: 0,
      breakdown,
      extras,
      totalCostTry: breakdown.totalTry,
      currency: 'TRY',
      costRate: unit,
      saleRate,
      saleAmountTry,
      quantity: qty
    };
  }

  if (method === COST_METHOD.FX_COST) {
    const fxRate = toFiniteNumber(usedRule?.costRate ?? usedRule?.manualCostRate);
    const qty = adet > 0 ? adet : 0;
    if (!(fxRate > 0) || !(qty > 0)) return fail([QUALITY_ISSUE.MISSING_FX_RATE]);
    const total = round2(fxRate * qty);
    breakdown.totalTry = total;
    currency = fxCurrencyFromProduct(product, islem);
    costRate = fxRate;
    quantity = qty;
    return {
      ok: true,
      costComplete: true,
      status: COST_STATUS_CODE.AUTOMATIC,
      issues,
      method,
      rule: usedRule,
      layer: resolved.layer,
      goldRate: rate,
      gram,
      adet,
      milyem: 0,
      breakdown,
      extras,
      totalCostTry: total,
      currency,
      costRate,
      saleRate: saleRate || toFiniteNumber(islem?.hasFiyati),
      saleAmountTry,
      quantity
    };
  }

  if (method === COST_METHOD.PURCHASE_AMOUNT) {
    const total = round2(toFiniteNumber(islem?.toplamTutar));
    if (!(total > 0)) return fail();
    breakdown.totalTry = total;
    return {
      ok: true,
      costComplete: true,
      status: COST_STATUS_CODE.AUTOMATIC,
      issues: [],
      method,
      rule: usedRule,
      layer: resolved.layer,
      goldRate: rate,
      gram,
      adet,
      milyem: 0,
      breakdown,
      extras,
      totalCostTry: total,
      currency: 'TRY',
      costRate: null,
      saleRate,
      saleAmountTry,
      quantity: adet || gram
    };
  }

  return fail([QUALITY_ISSUE.CALC_FAILED]);
}

function kategoriUsesGram(product, islem) {
  const kat = norm(product?.kategori || islem?.kategori);
  return kat === 'Hurda' || kat === 'Takı Ürün' || kat === 'Genel';
}

function fxCurrencyFromProduct(product, islem) {
  const name = norm(product?.isim || islem?.urun);
  const code = name.split(' - ')[0] || name.split(' ')[0];
  return code || 'FX';
}

export function buildFinanceSnapshot(calc, { computedAt, saleAmountTry } = {}) {
  const rule = calc?.rule;
  const breakdown = calc?.breakdown || emptyCostBreakdown();
  const complete = Boolean(calc?.costComplete && calc?.totalCostTry != null);
  const total = complete ? round2(calc.totalCostTry) : null;
  const parts = round2(
    (breakdown.goldTry || 0) +
      (breakdown.laborTry || 0) +
      (breakdown.stoneTry || 0) +
      (breakdown.additionalTry || 0)
  );
  if (complete && Math.abs(parts - (breakdown.totalTry || 0)) > 0.02) {
    breakdown.totalTry = parts;
  }

  const sale = saleAmountTry != null && saleAmountTry !== ''
    ? saleAmountTry
    : calc?.saleAmountTry;
  const side = calc?.tradeSide || 'sale';
  const economics = calc?.grossProfitTry != null && complete
    ? {
      saleAmountTry: sale == null || sale === '' ? null : round2(toFiniteNumber(sale)),
      grossProfitTry: round2(calc.grossProfitTry),
      grossMarginPct: calc.grossMarginPct == null ? null : round2(calc.grossMarginPct)
    }
    : computeTradeEconomics({
      amountTry: sale,
      referenceCostTry: total,
      costComplete: complete,
      side
    });

  return {
    schemaVersion: FINANCE_COST_SCHEMA_VERSION,
    costComplete: complete,
    costStatus: calc?.status || COST_STATUS_CODE.MISSING,
    unknownReasons: complete ? [] : (calc?.issues || [QUALITY_ISSUE.MISSING_RULE]),
    costRuleId: rule?.id || null,
    costRuleVersion: rule?.version || 1,
    costMethod: calc?.method || null,
    costLayer: calc?.layer || null,
    goldRateSource: calc?.goldRate?.source || null,
    hasAltinKuru: calc?.goldRate?.ok ? calc.goldRate.rate : null,
    currency: calc?.currency || null,
    quantity: calc?.quantity ?? null,
    costRate: calc?.costRate ?? null,
    saleRate: calc?.saleRate ?? null,
    gram: calc?.gram ?? null,
    milyem: calc?.milyem ?? null,
    laborModel: calc?.laborModel || null,
    costBreakdown: {
      goldTry: round2(breakdown.goldTry),
      laborTry: round2(breakdown.laborTry),
      stoneTry: round2(breakdown.stoneTry),
      additionalTry: round2(breakdown.additionalTry),
      totalTry: complete ? round2(breakdown.totalTry) : 0
    },
    totalCostTry: total,
    saleAmountTry: economics.amountTry != null ? economics.amountTry : economics.saleAmountTry,
    grossProfitTry: economics.grossProfitTry,
    grossMarginPct: economics.grossMarginPct,
    tradeSide: calc?.tradeSide || null,
    hamCode: calc?.hamCode || null,
    hamMultiplier: calc?.hamMultiplier || null,
    hamSellTry: calc?.hamSellTry || null,
    computedAt: computedAt || null
  };
}

/**
 * Barkodsuz / kuralsiz takida varsayilan milyemle gram formulu kurali uretir.
 * Hem satis anindaki snapshot hem de rapordaki anlik hesap ayni kurali
 * kullansin diye ortak: eskiden sadece raporda vardi, bu yuzden bazi
 * satislar "maliyet kurali yok" diye fotografsiz kaliyordu.
 */
export function buildTakiDefaultRule(islem, settings) {
  if (normalizeProductName(islem?.kategori) !== 'Takı Ürün') return null;
  const milyem = TAKI_DEFAULT_MILYEM[normalizeProductName(islem?.urun)];
  const gram = toFiniteNumber(islem?.gram);
  if (!(milyem > 0) || !(gram > 0)) return null;
  return {
    milyem,
    rule: {
      costMethod: COST_METHOD.GOLD_FORMULA,
      // Maliyet ayarlarindaki secim (varsayilan: has SATIS). Eskiden burada
      // LIVE_BUY sabitti; ayarda "satis" secili olsa bile alis fiyati kullaniliyordu.
      goldRateSource: settings?.defaultGoldRateSource || GOLD_RATE_SOURCE.LIVE_SELL,
      milyemOverride: milyem,
      active: true
    }
  };
}

/** Snapshot yoksa ham katalog ve gramı olan takı anlık hesaplanır. Gramı olmayan takı uydurulmaz. */
function resolveLiveHamFinance(islem, prices, settings) {
  if (!islem || !Array.isArray(prices) || !prices.length) return null;
  const product = {
    kategori: islem.kategori,
    isim: islem.urun,
    urunTipi: islem.urun,
    gram: islem.gram,
    isCatalog: true
  };
  let rule = null;
  if (!getHamCatalogSpec(product, islem)) {
    const fallback = buildTakiDefaultRule(islem, settings);
    if (!fallback) return null;
    product.urunMilyem = fallback.milyem;
    rule = fallback.rule;
  }
  const calc = calculateFinanceCost({
    product,
    islem,
    rule,
    rateContext: { prices, settings, defaultGoldRateSource: settings?.defaultGoldRateSource }
  });
  if (!calc.costComplete || calc.totalCostTry == null) return null;
  // calculateFinanceCost kari yalnizca HAM_VERI_SELL dalinda dolduruyor; gram
  // formuluyle maliyetlenen takida bos kaliyordu. Boylece anlik maliyetli
  // satislarin maliyeti toplamlara giriyor ama kari 0 sayiliyor, ekranda da
  // "Kar —" yaziyordu. Kar burada ayni kuralla hesaplanir:
  // satista tutar - maliyet, alista maliyet - tutar.
  const economics = computeTradeEconomics({
    amountTry: calc.saleAmountTry,
    referenceCostTry: calc.totalCostTry,
    costComplete: true,
    side: calc.tradeSide || tradeSideOf(islem)
  });
  return {
    ok: true,
    live: true,
    reason: rule ? 'live_taki_fallback' : 'live_ham_fallback',
    saleAmountTry: economics.amountTry != null ? economics.amountTry : calc.saleAmountTry,
    totalCostTry: calc.totalCostTry,
    grossProfitTry: calc.grossProfitTry != null ? calc.grossProfitTry : economics.grossProfitTry,
    grossMarginPct: calc.grossMarginPct != null ? calc.grossMarginPct : economics.grossMarginPct,
    schemaVersion: null
  };
}

export function resolveSaleFinance(islem, { prices, settings } = {}) {
  const snap = islem?.financeSnapshot;
  if (!snap) {
    const live = resolveLiveHamFinance(islem, prices, settings);
    if (live) return live;
    return {
      ok: false,
      reason: 'snapshot_missing',
      saleAmountTry: islem?.toplamTutar == null ? null : round2(toFiniteNumber(islem.toplamTutar)),
      totalCostTry: null,
      grossProfitTry: null,
      grossMarginPct: null,
      schemaVersion: null
    };
  }

  const version = Number(snap.schemaVersion) || 1;
  const costOk = snap.costComplete === true && snap.totalCostTry != null;
  const totalCostTry = costOk ? round2(snap.totalCostTry) : null;
  const saleFromSnap = snap.saleAmountTry != null && snap.saleAmountTry !== ''
    ? round2(toFiniteNumber(snap.saleAmountTry))
    : null;
  const saleFromIslem = islem?.toplamTutar != null && islem.toplamTutar !== ''
    ? round2(toFiniteNumber(islem.toplamTutar))
    : null;
  const saleAmountTry = saleFromSnap != null ? saleFromSnap : saleFromIslem;

  if (!costOk) {
    const live = resolveLiveHamFinance(islem, prices, settings);
    if (live) return live;
    return {
      ok: false,
      reason: 'snapshot_incomplete',
      saleAmountTry,
      totalCostTry: null,
      grossProfitTry: null,
      grossMarginPct: null,
      schemaVersion: version
    };
  }

  if (version >= 2 && snap.grossProfitTry != null) {
    const profit = round2(toFiniteNumber(snap.grossProfitTry));
    let margin = null;
    if (snap.grossMarginPct != null && saleAmountTry > 0) {
      margin = round2(toFiniteNumber(snap.grossMarginPct));
    } else if (saleAmountTry > 0) {
      margin = round2((profit / saleAmountTry) * 100);
    }
    return {
      ok: true,
      reason: null,
      saleAmountTry,
      totalCostTry,
      grossProfitTry: profit,
      grossMarginPct: margin,
      schemaVersion: version
    };
  }

  const economics = computeSaleEconomics({
    saleAmountTry,
    totalCostTry,
    costComplete: true
  });
  return {
    ok: true,
    reason: null,
    totalCostTry,
    schemaVersion: version,
    ...economics
  };
}

export function resolveSaleCostTry(islem, options) {
  const fin = resolveSaleFinance(islem, options);
  return { ok: fin.ok, totalCostTry: fin.totalCostTry, reason: fin.reason };
}

export function shouldAttachFinanceSnapshot(islem) {
  const tur = norm(islem?.islemTuru);
  if (islem?.hizliIslemTipi === 'bankaya_nakit_cik' || islem?.hizliIslemTipi === 'bankadan_kasa_nakit') {
    return false;
  }
  const urun = norm(islem?.urun);
  if (urun.includes('Bakiye Düzeltme') || urun.includes('Manuel Borç') || urun.includes('Manuel Alacak')) {
    return false;
  }
  return ['Satış', 'Alış', 'İade'].includes(tur);
}
